import './styles.scss'

import { memo, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { AutoComplete, Divider, Input, List } from 'antd'

import { ChevronRightIcon, CloseIcon, SearchIcon } from '@/components/Icons'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectSubmenuProductCategories } from '@/store/selectors/categorySelector'
import {
  selectIsSuggestionsFetching,
  selectSearchTerm,
  selectSuggestions,
} from '@/store/selectors/searchSelector'
import { trackOnSiteSearch } from '@/store/slices/adobeAnalyticSlice'
import { getSearchSuggestions, setSearchTerm } from '@/store/slices/searchSlice'
import useDeviceDetect from '@/hooks/useDeviceDetect'
import usePermission from '@/hooks/usePermission'
import { useSearchContext } from '@/contexts/search/useSearchContext'
import { SHOP_CATEGORY_TYPE } from '@/libs/constants'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'
import { formatMaterialID } from '@/libs/utils/material-number'

import A11yButton from '../A11yButton'
import B2BecLink from '../B2BLink'
import CustomButton from '../CustomButton'
import { generateSuggestionItemsLoading, generateSuggestionItemsTransparent } from './dummyData'
import SuggestionCategoryItem from './SuggestionCategoryItem'
import SuggestionItem from './SuggestionItem'

const { Search } = Input

const listOfCategoryDetergent = [
  {
    categoryId: 'accessories',
    categoryName: 'productSubCategoryOverview.accessories.title',
    type: 'accessories',
    menuImages: [
      {
        url: 'https://s1.kaercher-media.com/media/image/selection/1550/d3/accessory.webp',
        type: 'thumb',
      },
    ],
  },
  {
    categoryId: 'detergents',
    categoryName: 'productSubCategoryOverview.detergents.title',
    type: 'detergents',
    menuImages: [
      {
        url: 'https://s1.kaercher-media.com/media/image/selection/46310/d3/detergents.webp',
        type: 'thumb',
      },
    ],
  },
]

const suggestionCategoryKecIdList = [35_452, 35_427, 35_465, 71_458]

function Autocomplete() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isSearchFocused, setIsSearchFocused } = useSearchContext()
  const { isIpadLandScape } = useDeviceDetect()

  const productCategories = useAppSelector(selectSubmenuProductCategories)
  const searchTerm = useAppSelector(selectSearchTerm)
  const suggestions = useAppSelector(selectSuggestions)
  const isSuggestionsFetching = useAppSelector(selectIsSuggestionsFetching)
  const { hasPermission: canViewCategoriesList } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.USE_PRODUCT_NAVIGATION
  )

  const searchInputRef = useRef()
  const timeoutIdRef = useRef()

  const analyzeSearchHandler = useCallback(
    (totalResult = 0) => {
      dispatch(
        trackOnSiteSearch({
          onsiteSearchTerm: searchTerm,
          onsiteSearchResult: totalResult,
        }),
      )
    },
    [dispatch, searchTerm],
  )

  const suggestionCategories = useMemo(() => {
    const temporaryArray = [...listOfCategoryDetergent]
    if (isValidArray(productCategories)) {
      for (const category of productCategories) {
        const subCategories = category?.subCategories
        if (isValidArray(subCategories)) {
          for (const subCategory of subCategories) {
            if (suggestionCategoryKecIdList.includes(subCategory?.kecId)) {
              temporaryArray.unshift({
                ...subCategory,
                type: SHOP_CATEGORY_TYPE.MACHINES,
              })
            }
          }
        }
      }
    }

    return temporaryArray
  }, [productCategories])

  const renderSearchNotFoundContent = useCallback(
    () => (
      <p className="search-not-found__text">
        {t('searchNoResults')} <q>{searchTerm}</q>
      </p>
    ),
    [t, searchTerm],
  )

  const onFocusSearch = useCallback(() => {
    setIsSearchFocused(true)
  }, [setIsSearchFocused])

  const onBlurSearch = useCallback(() => {
    setIsSearchFocused(false)
  }, [setIsSearchFocused])

  const onBlurSearchAndInput = useCallback(() => {
    onBlurSearch()

    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.blur()
      }
    }, 0)
  }, [onBlurSearch])

  const onSearch = useCallback(
    (newSearchTerm) => {
      if (newSearchTerm) {
        if (isSearchFocused) {
          onBlurSearchAndInput()
        }

        navigate(linkGenerator(`/search-results/${newSearchTerm}`))
      }
    },
    [navigate, isSearchFocused, onBlurSearchAndInput],
  )

  const onSeeAllResults = useCallback(() => {
    onBlurSearchAndInput()

    navigate(linkGenerator(`/search-results/${searchTerm}`))
  }, [navigate, searchTerm, onBlurSearchAndInput])

  const options = useMemo(() => {
    if (isSuggestionsFetching) {
      return generateSuggestionItemsLoading(isIpadLandScape)
    }

    if (searchTerm === '') {
      // For Desktop view only:
      // When user focus on the search input => show empty space dropdown
      // on left column to make Specials For You on right column visible
      // while no searchKeyword is entered
      return generateSuggestionItemsTransparent(isIpadLandScape)
    }

    return suggestions.map(({ materialNumber, name, imageUrl }) => {
      const formattedMaterialNumber = formatMaterialID(
        materialNumber,
        materialNumber?.length,
      )

      return {
        value: formattedMaterialNumber,
        label: (
          <SuggestionItem
            imageUrl={imageUrl}
            isIpadLandScape={isIpadLandScape}
            materialNumber={formattedMaterialNumber}
            name={name}
          />
        ),
      }
    })
  }, [isSuggestionsFetching, isIpadLandScape, searchTerm, suggestions])

  const onSuggestionCategoryClick = useCallback(
    (categoryId, type) => {
      analyzeSearchHandler()
      onBlurSearchAndInput()
      if (categoryId) {
        const link = type === SHOP_CATEGORY_TYPE.MACHINES
          ? `/category/${categoryId}`
          : `/categories/${categoryId}`
        navigate(linkGenerator(link))
      }
    },
    [onBlurSearchAndInput, navigate, analyzeSearchHandler],
  )

  const handleSelectOption = useCallback(
    (value, option) => {
      const { name } = option?.label?.props || {}

      if (value && typeof value === 'string') {
        analyzeSearchHandler()
        onBlurSearchAndInput()
        dispatch(setSearchTerm(name))

        navigate(linkGenerator(`/product/${value}`))
      }
    },
    [dispatch, navigate, onBlurSearchAndInput, analyzeSearchHandler],
  )

  const onSearchChange = useCallback(
    // Wait for 300ms after users stop typing
    // then dispatch to get the suggestions
    (newSearchTerm) => {
      clearTimeout(timeoutIdRef.current)

      dispatch(setSearchTerm(newSearchTerm))

      timeoutIdRef.current = setTimeout(() => {
        if (newSearchTerm.length >= 2) {
          dispatch(getSearchSuggestions(newSearchTerm))
        }
      }, 300)
    },
    [dispatch],
  )

  const onInputKeyDown = useCallback(
    (e) => {
      // Check if users press Enter
      // then blur the search
      if (e.keyCode === 27) {
        onBlurSearchAndInput()
        e.preventDefault()
      }
    },
    [onBlurSearchAndInput],
  )

  const customMenu = (menu) => {
    return (
      <A11yButton elementType="div" onMouseDown={(e) => e.preventDefault()}>
        <div className="search__dropdown__wrapper">
          {isIpadLandScape
            ? (
              options.every((item) => !item?.label?.props?.isTransparent) && (
                <div className="search__dropdown__suggestion">{menu}</div>
              )
            )
            : <div className="search__dropdown__suggestion">{menu}</div>}
          {canViewCategoriesList && (!isIpadLandScape || searchTerm === '') && (
            <div className="search__dropdown__specials-for-you__wrapper">
              {!isIpadLandScape && (
                <div className="search__dropdown__specials-for-you__divider">
                  <Divider type="vertical" />
                </div>
              )}
              <div className="search__dropdown__product-categories__wrapper--inner">
                <div className="search__dropdown__product-categories__title">
                  {t('myWorkspace.productCategories.title')}
                </div>
                <div className="search__dropdown__product-categories__item__list">
                  <List
                    dataSource={suggestionCategories}
                    renderItem={(category) => (
                      <List.Item
                        key={category?.categoryId}
                        className="search__dropdown__product-categories__item"
                      >
                        <SuggestionCategoryItem
                          categoryId={category?.categoryId}
                          categoryName={category?.categoryName}
                          menuImages={category?.menuImages}
                          type={category?.type}
                          onClick={onSuggestionCategoryClick}
                        />
                      </List.Item>
                    )}
                  />
                </div>
                <p className="search__dropdown__product-categories__item__all-categories-link">
                  <B2BecLink to="/categories">
                    <ChevronRightIcon
                      style={{ width: 13, height: 13, marginRight: 10 }}
                    />
                    <span>
                      {t('myWorkspace.productCategories.seeAllCategories')}
                    </span>
                  </B2BecLink>
                </p>
              </div>
            </div>
          )}
        </div>
        {!isIpadLandScape
            && searchTerm.trim() !== ''
            && !isSuggestionsFetching
            && options.length > 0
          ? (
            <div className="see-all-results__wrapper">
              <CustomButton
                className="see-all-results__button fontBold"
                onClick={onSeeAllResults}
                onMouseDown={(e) => e.preventDefault()}
              >
                {t('seeAllResutls')}
              </CustomButton>
            </div>
          )
          : null}
      </A11yButton>
    )
  }

  const searchNotFound = options.length === 0 && searchTerm !== '' && !isSuggestionsFetching

  return (
    <div className="search__container" id="search__wrapper">
      <AutoComplete
        autoFocus={isIpadLandScape && isSearchFocused}
        defaultActiveFirstOption={false}
        popupRender={customMenu}
        getPopupContainer={() => document.querySelector('#search__wrapper')}
        listHeight={600}
        notFoundContent={searchNotFound ? renderSearchNotFoundContent() : null}
        open={isSearchFocused}
        options={options}
        className="suggestion-item-ant-select-item-option--override"
        suffixIcon={null}
        value={searchTerm}
        onBlur={onBlurSearch}
        onChange={onSearchChange}
        onFocus={onFocusSearch}
        onInputKeyDown={onInputKeyDown}
        onSelect={handleSelectOption}
      >
        <Search
          ref={searchInputRef}
          enterButton={isIpadLandScape ? <CloseIcon /> : t('buttonTexts.search')}
          placeholder={t('searchPlaceholder')}
          prefix={<SearchIcon />}
          size="large"
          type="search"
          onSearch={isIpadLandScape ? onBlurSearchAndInput : onSearch}
        />
      </AutoComplete>
    </div>
  )
}

export default memo(Autocomplete)
